import { supabase } from '@/integrations/supabase/client';

/**
 * Lightweight UI event tracker. Used to measure UX hypotheses such as:
 * - how often visitors open the "Sans créneaux" popover,
 * - whether opening that popover increases clicks toward the mediator profile.
 *
 * Strategy: best-effort insert into `activity_log` for authenticated users
 * (RLS allows `auth.uid() = user_id` inserts). For anonymous visitors we
 * fall back to a `console.info` so the events still surface in
 * client-side debugging / session replays without leaking to the DB.
 *
 * Never throws — analytics must not break the UI.
 */
export type UiEventName =
  | 'no_slots_badge_open'
  | 'no_slots_profile_click'
  | 'mediator_profile_click';

export interface UiEventPayload {
  /** Mediator the event is about, when applicable. */
  mediatorId?: string | null;
  /** Where the event originated (e.g. 'search_widget', 'seeker_page'). */
  source?: string;
  /** Free-form extra context (kept short, stored as the description). */
  context?: Record<string, unknown>;
}

export const trackUiEvent = async (
  name: UiEventName,
  payload: UiEventPayload = {},
): Promise<void> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth.user?.id ?? null;

    const description = JSON.stringify({
      source: payload.source,
      ...payload.context,
    });

    if (userId) {
      // Best-effort; ignore failures so analytics never bubbles up.
      await supabase.from('activity_log').insert({
        user_id: userId,
        activity_type: 'ui_event',
        title: name,
        description,
        related_id: payload.mediatorId ?? null,
      });
    } else {
      // Anonymous visitor — log locally only.
      // eslint-disable-next-line no-console
      console.info('[ui_event]', name, {
        mediatorId: payload.mediatorId ?? null,
        source: payload.source,
        ...payload.context,
      });
    }
  } catch {
    // Analytics is fire-and-forget.
  }
};
